import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FolderOpen } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { VideoModal, ImageModal, AuthPrompt } from "@/components/ui";
import {
  AssetGrid,
  AssetEmptyState,
  AssetGridSkeleton,
  AssetsHeader,
  AssetsPagination,
  SlowLoadingMessage,
  type AssetFilterType,
} from "@/components/features/assets";
import { CharacterGrid } from "@/components/features/characters";
import { PiecanNotice } from "@/components/features/piecan-notice";
import { useUserStore, getDisplayUrl } from "@/stores/user-store";
import type { Asset } from "@/types";
import { cn } from "@/lib/utils";
import {
  useAssets,
  useCharacters,
  useFavoriteAsset,
  useDeleteAsset,
} from "@/hooks";
import { useBackgroundProcessing } from "@/hooks/use-background-processing";
import { mapAssetForDisplay, filterAssets } from "@/lib/asset-utils";
import { trackPageView } from "@/services/analytics";

export function AssetsPage() {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded: isClerkLoaded, user } = useUser();
  const { plan } = useUserStore();

  const [filter, setFilter] = useState<AssetFilterType>("all");
  const [displayFilter, setDisplayFilter] = useState<AssetFilterType>("all"); // What's actually shown (lags for animation)
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [page, setPage] = useState(1);

  // Track page view
  useEffect(() => {
    trackPageView('Assets', 'My Assets', user?.id, { filter })
  }, [user?.id, filter])

  // Animation tracking
  const seenAssetIdsRef = useRef<Set<string>>(new Set());
  const hasInitializedRef = useRef(false);
  const [newAssetIds, setNewAssetIds] = useState<Set<string>>(new Set());
  const [isExiting, setIsExiting] = useState(false);
  const [showSlowLoadingMessage, setShowSlowLoadingMessage] = useState(false);
  const slowLoadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // The Assets page is the user's general library — character-bound
  // generations live under /characters and are filtered out here via
  // the default `bare=true` on the backend.
  const { data, isFetching, refetch } = useAssets({
    page,
    perPage: 50,
    source: "generation",
  });

  // Pre-warm the characters cache on page mount so clicking the
  // Characters tab renders instantly instead of flashing skeletons.
  // CharacterGrid calls `useCharacters` itself; same query key, so
  // its query reads from this cache rather than re-fetching cold.
  useCharacters();

  // Show slow loading message after 5 seconds
  useEffect(() => {
    if (isFetching) {
      slowLoadingTimerRef.current = setTimeout(
        () => setShowSlowLoadingMessage(true),
        5000,
      );
    } else {
      if (slowLoadingTimerRef.current) {
        clearTimeout(slowLoadingTimerRef.current);
        slowLoadingTimerRef.current = null;
      }
      setShowSlowLoadingMessage(false);
    }

    return () => {
      if (slowLoadingTimerRef.current) {
        clearTimeout(slowLoadingTimerRef.current);
      }
    };
  }, [isFetching]);

  const favoriteMutation = useFavoriteAsset();
  const deleteMutation = useDeleteAsset();

  const assets = data?.assets ?? [];
  const filteredAssets = useMemo(
    () => filterAssets(assets, displayFilter),
    [assets, displayFilter],
  );

  // Trigger background processing for processing generations
  useBackgroundProcessing(assets);

  // Handle filter change with animations
  const handleFilterChange = (newFilter: AssetFilterType) => {
    if (!hasInitializedRef.current || newFilter === filter || isFetching) {
      setFilter(newFilter);
      setDisplayFilter(newFilter);
      setPage(1);
      return;
    }

    // Two distinct data sources back the tabs:
    //   "generation" — /asset/:user_id (most tabs)
    //   "characters" — /characters/:user_id (Characters tab)
    // The fade-out + fade-in animation only looks right when both
    // tabs share the same source. A cross-source switch would render
    // skeletons mid-animation and pop into final cards a moment
    // later — janky. Skip the animation in that case.
    const sourceFor = (f: AssetFilterType): "generation" | "characters" =>
      f === "characters" ? "characters" : "generation";
    const sourceChanges = sourceFor(displayFilter) !== sourceFor(newFilter);

    // Update tab indicator immediately
    setFilter(newFilter);
    setPage(1);

    if (sourceChanges) {
      // Skip animation when source changes (requires new API call)
      setDisplayFilter(newFilter);
      return;
    }

    // Same source - animate
    setIsExiting(true);
    setTimeout(() => {
      setDisplayFilter(newFilter);
      setIsExiting(false);
    }, 300);
  };

  // Animate genuinely-new assets (e.g., a generation just completed)
  // with the slow `card-in` animation. We deliberately do NOT animate
  // cards on a filter change — the page-wrapper fade is already the
  // tab-switch animation, and stacking a 1s per-card animation on
  // top of it produces a visible flicker (cards keep scaling /
  // sliding into place long after the page fade has finished).
  useEffect(() => {
    if (!filteredAssets.length) return;

    const filteredIds = new Set(filteredAssets.map((a) => a.id));

    if (!hasInitializedRef.current) {
      seenAssetIdsRef.current = filteredIds;
      hasInitializedRef.current = true;
      return;
    }

    if (isFetching) {
      // Track in-flight ids so an interim fetch doesn't suddenly
      // animate cards we already have on screen.
      seenAssetIdsRef.current = new Set([
        ...seenAssetIdsRef.current,
        ...filteredIds,
      ]);
      return;
    }

    const newIds = new Set<string>();
    for (const id of filteredIds) {
      if (!seenAssetIdsRef.current.has(id)) {
        newIds.add(id);
      }
    }

    if (newIds.size > 0) {
      setNewAssetIds(newIds);
      const timeout = setTimeout(() => setNewAssetIds(new Set()), 1200);
      seenAssetIdsRef.current = new Set([
        ...seenAssetIdsRef.current,
        ...filteredIds,
      ]);
      return () => clearTimeout(timeout);
    }

    seenAssetIdsRef.current = new Set([
      ...seenAssetIdsRef.current,
      ...filteredIds,
    ]);
  }, [filteredAssets, isFetching]);

  const isReady = isClerkLoaded && (!isSignedIn || data !== undefined);

  // Content rendering
  let content: React.ReactNode;

  if (!isClerkLoaded || !isReady) {
    content = (
      <div className="space-y-4">
        {showSlowLoadingMessage && <SlowLoadingMessage show={true} />}
        <AssetGridSkeleton count={6} />
      </div>
    );
  } else if (!isSignedIn) {
    content = (
      <AuthPrompt
        message="Sign up or log in to view your assets"
        icon={FolderOpen}
      />
    );
  } else if (filter === "characters") {
    // Characters tab on /assets is the library/discovery view. Card
    // clicks open the character workflow at /characters/<name>; the
    // empty-state "Create" CTA fires straight into the creation flow.
    content = (
      <CharacterGrid onCreateClick={() => navigate("/characters")} />
    );
  } else if (assets.length === 0) {
    content = <AssetEmptyState filter="all" onNavigate={navigate} fullPage />;
  } else if (filteredAssets.length === 0) {
    content = <AssetEmptyState filter={filter} onNavigate={navigate} />;
  } else {
    content = (
      <AssetGrid
        assets={filteredAssets.map((asset) => mapAssetForDisplay(asset, plan))}
        onPlay={setSelectedAsset}
        onToggleFavorite={(id) => favoriteMutation.mutate(id)}
        onCopyPrompt={(prompt) => navigator.clipboard.writeText(prompt)}
        onDelete={(id) => deleteMutation.mutate(id)}
        newAssetIds={newAssetIds}
      />
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <AssetsHeader
        filter={filter}
        isFetching={isFetching}
        isExiting={isExiting}
        onRefresh={() => refetch()}
        onFilterChange={handleFilterChange}
      />

      {/* Partner upsell — hidden for gated / signed-out users. Sits under the
          header so upload-capable users see the "try PieCan" nudge before
          scanning their library. */}
      <div className="mt-4 mb-2">
        <PiecanNotice source="assets_notice" />
      </div>

      {/* Slow loading message */}
      {showSlowLoadingMessage && isReady && isFetching && (
        <SlowLoadingMessage show={true} />
      )}

      <div
        className={cn(
          "transition-all duration-300",
          isExiting && "opacity-0 scale-[0.98] translate-y-2",
        )}
      >
        {content}
      </div>

      {/* Pagination — hidden on Characters tab since CharacterGrid does
          its own data fetching and isn't paginated. */}
      {data?.pagination && filter !== "characters" && (
        <AssetsPagination
          currentPage={page}
          totalPages={data.pagination.total_pages}
          onPageChange={setPage}
        />
      )}

      {/* Modal */}
      {selectedAsset?.media_type === "VIDEO" ? (
        <VideoModal
          isOpen={!!selectedAsset}
          onClose={() => setSelectedAsset(null)}
          videoUrl={getDisplayUrl(selectedAsset, plan)}
          title={selectedAsset.prompt ?? "Video"}
          dimensions={{
            width: selectedAsset.width,
            height: selectedAsset.height,
          }}
        />
      ) : selectedAsset ? (
        <ImageModal
          isOpen={!!selectedAsset}
          onClose={() => setSelectedAsset(null)}
          imageUrl={getDisplayUrl(selectedAsset, plan)}
          title={selectedAsset.prompt ?? "Image"}
        />
      ) : null}
    </div>
  );
}
